/**
 * RotatePasswordPage — first-login forced password reset (USR-04).
 *
 * Reachable only when authenticated (ProtectedRoute lets it through even when
 * ``password_must_rotate=true``). On 204, refetches /auth/me, updates the auth
 * store with the new ``password_must_rotate=false``, and navigates to ``"/"``
 * (App.tsx then routes "/" → /positions as the role-agnostic landing per BUG-061).
 *
 * BUG-022: zod min(8) / max(128) on new_password mirrors the backend pydantic
 * RotatePasswordRequest from plan 01-03. HTML constraints paired throughout.
 */
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { toast } from "sonner";
import { z } from "zod";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { PasswordInput } from "@/components/ui/password-input";
import { LanguageChips } from "@/components/brand/LanguageChips";
import { Wordmark } from "@/components/brand/Wordmark";
import { apiFetch, formatValidationError } from "@/lib/api";
import { useAuthStore } from "@/lib/auth";
import { TOKENS } from "@/lib/tokens";
import { ApiError } from "@/types/api";
import type { UserPublic } from "@/types/api";

const schema = z.object({
  current_password: z.string().min(1).max(128),
  new_password: z.string().min(8).max(128),
});

type RotateValues = z.infer<typeof schema>;

export function RotatePasswordPage() {
  const { t } = useTranslation(["auth", "common"]);
  const navigate = useNavigate();
  const currentUser = useAuthStore((s) => s.user);
  const login = useAuthStore((s) => s.login);
  const token = useAuthStore((s) => s.token);

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isValid, isSubmitting },
  } = useForm<RotateValues>({
    resolver: zodResolver(schema),
    mode: "onChange",
    defaultValues: { current_password: "", new_password: "" },
  });

  async function onSubmit(values: RotateValues) {
    try {
      await apiFetch<void>("/auth/rotate-password", {
        method: "POST",
        body: JSON.stringify(values),
      });
      // Refresh user — must_rotate is now false.
      const fresh = await apiFetch<UserPublic>("/auth/me");
      if (token) {
        login(token, fresh);
      }
      // BUG-061: default landing is "/" which App.tsx routes to /positions (role-agnostic).
      // /admin/users is gated to Content Admin; sending every user there 403'd hr_recruiter.
      navigate("/", { replace: true });
    } catch (err) {
      // BUG-035: backend returns 400 (NOT 401) for wrong current_password so the
      // global apiFetch 401 handler doesn't kick in and silently log the user
      // out before they see the inline "wrong current password" message.
      if (
        err instanceof ApiError &&
        err.status === 400 &&
        err.body.detail === "wrong_current_password"
      ) {
        setError("current_password", {
          message: t("auth:rotate.errors.wrong_current"),
        });
        return;
      }
      toast.error(formatValidationError(err));
    }
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center p-6"
      style={{
        backgroundImage: `linear-gradient(135deg, ${TOKENS.depth} 0%, ${TOKENS.depthElevated} 100%)`,
      }}
    >
      <Card
        className="w-full max-w-md mx-auto"
        style={{
          backgroundColor: TOKENS.surfaceElevated,
          boxShadow: TOKENS.shadowStrong,
          border: `1px solid ${TOKENS.border}`,
          borderRadius: TOKENS.radius,
        }}
      >
        <CardHeader
          className="flex flex-row items-center justify-between"
          style={{
            minHeight: "56px",
            borderBottom: `2px solid ${TOKENS.primary}`,
          }}
        >
          <Wordmark size="md" />
          <LanguageChips />
        </CardHeader>
        <CardContent>
          <h1
            className="text-xl font-semibold mb-2"
            style={{ color: TOKENS.text }}
          >
            {t("auth:rotate.title")}
          </h1>
          <p
            className="text-sm mb-4"
            style={{ color: TOKENS.textMuted }}
          >
            {t("auth:rotate.subtitle")}
            {currentUser ? ` (${currentUser.username})` : ""}
          </p>
          <form
            noValidate
            onSubmit={handleSubmit(onSubmit)}
            className="space-y-4"
            style={{ marginTop: "1rem" }}
          >
            <div>
              <Label htmlFor="current_password">
                {t("auth:rotate.current")}
              </Label>
              <PasswordInput
                id="current_password"
                autoComplete="current-password"
                required
                minLength={1}
                maxLength={128}
                aria-invalid={errors.current_password ? "true" : "false"}
                {...register("current_password")}
              />
              {errors.current_password && (
                <p className="text-sm mt-1" style={{ color: "#dc2626" }}>
                  {errors.current_password.message ||
                    t("common:errors.invalid_input")}
                </p>
              )}
            </div>
            <div>
              <Label htmlFor="new_password">{t("auth:rotate.new")}</Label>
              <PasswordInput
                id="new_password"
                autoComplete="new-password"
                required
                minLength={8}
                maxLength={128}
                aria-invalid={errors.new_password ? "true" : "false"}
                {...register("new_password")}
              />
              {errors.new_password && (
                <p className="text-sm mt-1" style={{ color: "#dc2626" }}>
                  {t("auth:rotate.min_8_chars")}
                </p>
              )}
            </div>
            {!isValid && (
              <p
                className="text-xs"
                role="status"
                style={{ color: TOKENS.textMuted }}
              >
                {t("auth:rotate.fix_errors_to_continue")}
              </p>
            )}
            <Button
              type="submit"
              disabled={!isValid || isSubmitting}
              className="w-full"
              style={{
                backgroundColor: TOKENS.primary,
                color: TOKENS.primaryFg,
                minHeight: "44px",
                fontWeight: 600,
              }}
            >
              {isSubmitting
                ? t("common:actions.loading")
                : t("auth:rotate.submit")}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
